import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

interface Pro20SuccessEnvelope<T> {
  success: boolean;
  message?: string;
  results: T;
}

function isPro20Envelope<T>(
  response: unknown,
): response is Pro20ApiResponse<T> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'code' in response &&
    'results' in response &&
    typeof (response as Pro20ApiResponse<T>).code === 'number'
  );
}

function isSuccessEnvelope<T>(
  response: unknown,
): response is Pro20SuccessEnvelope<T> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    'results' in response &&
    (response as Pro20SuccessEnvelope<T>).success
  );
}

/**
 * Unwraps PRO20 `{ code, message, results }` responses.
 * Accepts any 2xx business code (e.g. 200, 201).
 */
export function unwrapPro20Response<T>(response: Pro20ApiResponse<T>): T {
  if (response.code < 200 || response.code >= 300) {
    throw new Error(response.message || 'Request failed');
  }
  return response.results;
}

/**
 * Accepts PRO20 `{ code, results }`, `{ success, results }`, or a direct payload.
 */
export function normalizePro20Response<T>(
  response: Pro20ApiResponse<T> | Pro20SuccessEnvelope<T> | T,
): T {
  if (isPro20Envelope<T>(response)) {
    return unwrapPro20Response(response);
  }

  if (isSuccessEnvelope<T>(response)) {
    return response.results;
  }

  return response;
}
